'use client';

import { useMemo, useState } from 'react';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import { WithClassName } from 'src/utils/UtilityTypes';
import { cm } from 'src/utils/classNames';
import MarketIcon from '../Utils/MarketIcon';
import { Drift } from '@drift-labs/icons';
import { getColorVar } from 'src/utils/theme/colorVars';
import { ComboBox } from 'src/components/primitives/ComboBox/ComboBox';
import { UIMarket } from '@drift/common';
import { MarketType } from '@drift-labs/sdk';

interface Props extends WithClassName {
	id: string;
	scrollContainerClassName?: string;
	currentSelection: string;
	options: AssetListAssetWithMarketIndex[];
	onChange: (value: string) => void;
	label: string;
}

export function AssetFilterWithSearch({
	id,
	currentSelection,
	options,
	onChange,
	label,
	className,
	scrollContainerClassName,
}: Props) {
	const [searchTerm, setSearchTerm] = useState('');

	const selectedOption = useMemo(() => {
		return options.find((option) => option.ticker === currentSelection);
	}, [options, currentSelection]);

	const filteredOptions = useMemo(() => {
		if (!searchTerm) return options;

		const term = searchTerm.toLowerCase();
		return options.filter(
			(option) =>
				option.ticker.toLowerCase().includes(term) ||
				option.displayLabel?.toLowerCase().includes(term)
		);
	}, [options, searchTerm]);

	const [isOpen, setIsOpen] = useState(false);

	const selectedOptionIsSpot = selectedOption?.mint !== undefined;

	return (
		<ComboBox.Root open={isOpen} onOpenChange={setIsOpen}>
			<ComboBox.Trigger label={label} className={className}>
				{selectedOption?.ticker === 'all_markets' ? (
					<Drift color={getColorVar('text-secondary')} size={16} />
				) : (
					selectedOption && (
						<MarketIcon
							marketSymbol={getOptionDisplaySymbol(
								selectedOption,
								selectedOptionIsSpot ? MarketType.SPOT : MarketType.PERP
							)}
							className="size-4 rounded-full"
							useNextImageComponent
						/>
					)
				)}
				<span className="overflow-hidden text-ellipsis">
					{selectedOption
						? selectedOption?.displayLabel || selectedOption?.ticker
						: 'Select Market'}
				</span>
			</ComboBox.Trigger>

			<ComboBox.Options
				id={id}
				query={searchTerm}
				setQuery={setSearchTerm}
				value={currentSelection}
				onValueChange={onChange}
				searchBarPlaceholder="Search"
				scrollContainerClassName={cm('max-h-[150px]', scrollContainerClassName)}
			>
				{filteredOptions.map((option) => {
					const displaySymbol = getOptionDisplaySymbol(
						option,
						option.mint ? MarketType.SPOT : MarketType.PERP
					);

					return (
						<ComboBox.Option
							key={option.ticker}
							value={option.ticker}
							onValueChange={(value) => {
								onChange(value);
								setIsOpen(false);
							}}
							isSelected={currentSelection === option.ticker}
						>
							<div className="flex items-center gap-x-xs">
								{option.ticker === 'all_markets' ? (
									<Drift
										className="size-full"
										color={getColorVar('text-secondary')}
										size={16}
									/>
								) : (
									<MarketIcon
										marketSymbol={displaySymbol}
										className="size-4 rounded-full"
										useNextImageComponent
									/>
								)}
								{option.displayLabel || option.ticker}
							</div>
						</ComboBox.Option>
					);
				})}
			</ComboBox.Options>
		</ComboBox.Root>
	);
}

function getOptionDisplaySymbol(
	option: AssetListAssetWithMarketIndex,
	marketType: MarketType
): string {
	if (option.marketIndex === -1) {
		return option.displayLabel || option.ticker;
	}

	try {
		return new UIMarket(option.marketIndex, marketType).uiSymbols
			.marketDisplaySymbol;
	} catch (error) {
		return option.displayLabel || option.ticker;
	}
}
